import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, Inject, OnInit, signal} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn
} from '@angular/forms';
import {MatDialogModule, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {find} from 'lodash';
import {OnSubmitErrorStateMatcher} from '../../../core/helpers/on-submit-error-state-matcher';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {RenumberService} from '../../../diagram-interaction/services/renumber.service';
import {
  DialogCloseButtonComponent,
  InfoMessageComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent,
  TextFieldComponent
} from '@q9elements/ui-kit/components';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {SnackBarService} from '@q9elements/ui-kit/common';

function compareIntegers(a: string, b: string): boolean {
  return parseInt(a, 10) === parseInt(b, 10);
}

@Component({
  selector: 'app-activity-renumber-dialog',
  templateUrl: './activity-renumber-dialog.component.html',
  styleUrls: ['./activity-renumber-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    TranslateModule,
    DialogCloseButtonComponent,
    InfoMessageComponent,
    TextFieldComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent
  ]
})
export class ActivityRenumberDialogComponent implements OnInit {
  public form: FormGroup;
  public processing = signal(false);
  public matcher = new OnSubmitErrorStateMatcher();

  constructor(
    private translateSvc: TranslateService,
    private renumberSvc: RenumberService,
    public diagramJsonSvc: DiagramJsonHandlerService,
    private snackBarSvc: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public cellId: any,
    private dialogRef: MatDialogRef<ActivityRenumberDialogComponent>,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      number: [
        '',
        [
          Q9Validators.required(this.translateSvc.instant('RENUMBER_ACT.E_REQUIRED')),
          Q9Validators.min(1),
          Q9Validators.max(999),
          this.isNumberValidator(),
          this.numberInUseAndNotProValidator(),
          this.sameNumberValidator()
        ]
      ]
    });
  }

  async onSubmit(): Promise<void> {
    if (this.form.valid && !this.processing()) {
      this.processing.set(true);
      const value = this.form.get('number').value;
      const usedActivity = this.numberInUse(value);
      const currentActivity = this.findActivityByMxKey(this.cellId);

      if (usedActivity) {
        const list = [
          {mxKey: this.cellId, number: parseInt(value, 10)},
          {mxKey: usedActivity.mxKey, number: parseInt(currentActivity.number, 10)}
        ];

        return this.renumberSvc.swapActivity(list).then(() => {
          this.snackBarSvc.success('RENUMBER_ACT.ACTIVITY_NUMBER_SWAPPED');

          return this.dialogRef.close();
        });
      }

      try {
        await this.renumberSvc.renumberActivity(this.cellId, value);
        this.dialogRef.close();
      } catch (error) {
        this.processing.set(false);
      }
    }
  }

  private isNumberValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      if (parseInt(control.value, 10).toString() === control.value) {
        return null;
      } else {
        return {
          notNumber: {
            message: this.translateSvc.instant('RENUMBER_ACT.E_PATTERN', {min: 1, max: 999})
          }
        };
      }
    };
  }

  private findActivityByMxKey(mxKey: string) {
    const json = this.diagramJsonSvc.get();

    return find(json.activities, activity => {
      return activity && compareIntegers(activity.mxKey, mxKey);
    });
  }

  private numberInUse(value: string) {
    const json = this.diagramJsonSvc.get();

    return find(json.activities, activity => {
      return activity && compareIntegers(activity.number, value);
    });
  }

  private numberInUseAndNotProValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const isPro = this.diagramJsonSvc.isPro();
      const numberIsInUse = this.numberInUse(control.value);

      if (numberIsInUse && !isPro) {
        return {
          numberInUseAndNotPro: {
            message: this.translateSvc.instant('RENUMBER_ACT.E_ALREADY_IN_USE', {
              number: this.form.get('number').value
            })
          }
        };
      } else {
        return null;
      }
    };
  }

  private sameNumberValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = control.value;
      const json = this.diagramJsonSvc.get();
      const sameNumber = find(json.activities, activity => {
        return (
          activity &&
          compareIntegers(activity.number, value) &&
          compareIntegers(activity.mxKey, this.cellId)
        );
      });

      if (sameNumber) {
        return {sameNumber: {message: this.translateSvc.instant('RENUMBER_ACT.E_SAME_NUMBER')}};
      } else {
        return null;
      }
    };
  }
}
